<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CustomerContextAPI from '../../../api/integrations/customerContext';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

// Ellie Desk: customer context rendered as part of the contact header card
// (replaces the old bottom-of-sidebar CustomerContextPanel accordion).
// Still a generic renderer over whatever ellie-desk-ai's provider engine
// returns — see plans/ellie-desk-customer-context-plan.md §2a. A new
// provider needs zero changes here: badge facts become pills, other facts
// become rows, and sections stay behind the details toggle.
const context = ref(null);
const loading = ref(true);
const refreshing = ref(false);
const error = ref('');
const showDetails = ref(false);

const providers = computed(() => context.value?.providers || []);
const okProviders = computed(() =>
  providers.value.filter(provider => provider.status === 'ok')
);
const ambiguousProviders = computed(() =>
  providers.value.filter(provider => provider.status === 'ambiguous')
);
// Provider-level errors surface so agents can tell a broken integration from
// a genuine no-match; not_found/not_configured stay hidden — they're the
// normal case for most contacts and would reintroduce the clutter this card
// removes.
const errorProviders = computed(() =>
  providers.value.filter(provider => provider.status === 'error')
);
const hasAnyData = computed(
  () =>
    okProviders.value.length > 0 ||
    ambiguousProviders.value.length > 0 ||
    errorProviders.value.length > 0
);
const hasDetails = computed(() =>
  okProviders.value.some(provider => provider.sections?.length)
);
const emptyMessage = computed(() =>
  providers.value.length
    ? t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.NO_DATA')
    : t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.NO_PROVIDERS')
);

const presentFacts = provider =>
  (provider.facts || []).filter(
    fact => fact.value !== null && fact.value !== ''
  );
const badgeFacts = provider =>
  presentFacts(provider).filter(fact => fact.kind === 'badge');
const rowFacts = provider =>
  presentFacts(provider).filter(fact => fact.kind !== 'badge');

const badgeClass = sentiment => {
  const classes = {
    good: 'bg-n-teal-3 text-n-teal-11',
    warn: 'bg-n-amber-3 text-n-amber-11',
    bad: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return classes[sentiment] || 'bg-n-alpha-2 text-n-slate-12';
};

const sentimentClass = sentiment => {
  const classes = {
    good: 'text-n-teal-9',
    warn: 'text-n-amber-9',
    bad: 'text-n-ruby-9',
  };
  return classes[sentiment] || 'text-n-slate-12';
};

const fetchContext = async () => {
  try {
    loading.value = true;
    error.value = '';
    const response = await CustomerContextAPI.get(props.conversationId);
    context.value = response.data;
  } catch (e) {
    error.value =
      e.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.ERROR');
  } finally {
    loading.value = false;
  }
};

const refresh = async () => {
  try {
    refreshing.value = true;
    error.value = '';
    const response = await CustomerContextAPI.refresh(props.conversationId);
    context.value = response.data;
  } catch (e) {
    error.value =
      e.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.ERROR');
  } finally {
    refreshing.value = false;
  }
};

watch(() => props.conversationId, fetchContext, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-2 px-4 pb-4">
    <div class="flex items-center justify-between">
      <span class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
        {{ $t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.TITLE') }}
      </span>
      <Button
        v-tooltip.left="$t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.REFRESH')"
        ghost
        xs
        slate
        icon="i-lucide-refresh-cw"
        :is-loading="refreshing"
        :disabled="refreshing"
        @click="refresh"
      />
    </div>

    <div v-if="loading" class="flex justify-center py-2">
      <Spinner class="size-5 text-n-brand" />
    </div>

    <div v-else-if="error" class="flex items-center gap-2 text-xs">
      <span class="text-n-ruby-11">{{ error }}</span>
      <button class="text-n-slate-11 underline" @click="fetchContext">
        {{ $t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.RETRY') }}
      </button>
    </div>

    <template v-else>
      <p v-if="!hasAnyData" class="mb-0 text-xs text-n-slate-11">
        {{ emptyMessage }}
      </p>

      <div
        v-for="provider in okProviders"
        :key="provider.provider"
        class="flex flex-col gap-1.5"
      >
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-xs text-n-slate-10">
            {{ provider.displayName }}
          </span>
          <div class="flex items-center gap-2">
            <a
              v-for="link in provider.links"
              :key="link.url"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-1 text-xs text-n-slate-11 hover:underline"
            >
              {{ link.label }}
              <i class="i-lucide-external-link" />
            </a>
          </div>
        </div>

        <div
          v-if="badgeFacts(provider).length"
          class="flex flex-wrap items-center gap-1"
        >
          <span
            v-for="fact in badgeFacts(provider)"
            :key="fact.key"
            v-tooltip.top="fact.label"
            class="rounded-md px-1.5 py-0.5 text-xs font-medium"
            :class="badgeClass(fact.sentiment)"
          >
            {{ fact.value }}
          </span>
        </div>

        <div
          v-for="fact in rowFacts(provider)"
          :key="fact.key"
          class="flex justify-between gap-2 text-sm"
        >
          <span class="text-n-slate-11">{{ fact.label }}</span>
          <span :class="sentimentClass(fact.sentiment)" class="font-medium">
            {{ fact.value }}
          </span>
        </div>
      </div>

      <p
        v-for="provider in ambiguousProviders"
        :key="provider.provider"
        class="mb-0 text-xs text-n-slate-11"
      >
        {{
          $t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.AMBIGUOUS_PROVIDER', {
            provider: provider.displayName,
            count: provider.ambiguousCount,
          })
        }}
      </p>

      <p
        v-for="provider in errorProviders"
        :key="`error-${provider.provider}`"
        class="mb-0 text-xs text-n-ruby-11"
      >
        {{
          $t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.PROVIDER_ERROR', {
            provider: provider.displayName,
          })
        }}
      </p>

      <button
        v-if="hasDetails"
        class="flex items-center gap-1 self-start text-xs text-n-slate-11 hover:text-n-slate-12"
        @click="showDetails = !showDetails"
      >
        <i
          :class="showDetails ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        />
        {{
          showDetails
            ? $t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.HIDE_DETAILS')
            : $t('CONVERSATION_SIDEBAR.CUSTOMER_CONTEXT.SHOW_DETAILS')
        }}
      </button>

      <template v-if="showDetails">
        <div
          v-for="provider in okProviders"
          :key="`details-${provider.provider}`"
          class="flex flex-col gap-1"
        >
          <table
            v-for="section in provider.sections"
            :key="section.key"
            class="w-full text-xs"
          >
            <caption class="pb-1 text-left text-n-slate-11 caption-top">
              {{
                section.label
              }}
            </caption>
            <tbody>
              <tr
                v-for="(row, index) in section.rows"
                :key="index"
                class="border-t border-n-weak"
              >
                <td
                  v-for="column in section.columns || Object.keys(row)"
                  :key="column"
                  class="py-1 pr-2 text-n-slate-12"
                >
                  {{ row[column] }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>
